import CloseIcon from '@mui/icons-material/Close'
import { Box, CircularProgress, FormHelperText } from '@mui/material'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import CustomDialog from 'common/components/CustomDialog'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { esES } from '@mui/x-date-pickers/locales'
import 'dayjs/locale/es'
import {
  consultaInicial,
  executeFieldAction,
  selectCamposSolicitud,
  selectDatosSolicitud,
  setCampoCargando,
  setCampoError,
  setCampoValidando,
} from 'features/solicitudes/solicitudesSlice'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { validate } from '../../libs/FieldResquestValidator'
import { processValidationResult, todosLosPadresTienenValor } from '../../libs/SolicitudesHelpers'
import LabelComponent from '../LabelComponent'
import ValidatingText from '../ValidatingText'
import CustomDateTimeComponent from './CustomDateTimeComponent'

function DateComponent({ idpk }) {
  const dispatch = useDispatch()
  const campos = useSelector(selectCamposSolicitud)
  const campo = campos.filter((c) => c.idpk === idpk)[0]

  const datos = useSelector(selectDatosSolicitud)
  const valor = datos[campo.idpk]

  const [validationError, setValidationError] = useState('')
  const [alertOpen, setAlertOpen] = useState(false)
  const [warningDialog, setWarningDialog] = useState({
    open: false,
    message: '',
    pendingValue: null,
  })

  const getValoresConsulta = () => {
    const valores = []
    for (const idpk in datos) {
      if (Object.hasOwnProperty.call(datos, idpk)) {
        const valor = datos[idpk]
        const field = campos.filter((c) => Number(c.idpk) === Number(idpk))[0]
        valores.push({ codigo_campo: field.codigo_campo, valor, tipo: field.tipo_dato })
      }
    }
    return valores
  }

  // La inicialización ahora se hace de forma centralizada en CamposSolicitud
  // mediante el endpoint /inicializasolicitud/

  useEffect(() => {
    if (valor) {
      dispatch(setCampoCargando({ idpk: campo.idpk, cargando: false }))
    }
    // Lanzamos las queries iniciales para campos dependientes solo si:
    // 1. El campo tiene valor
    // 2. El campo dependiente necesita inicialización (inicializando === 'P')
    // 3. El campo dependiente NO está siendo inicializado por inicializaSolicitud (inicializando !== 'R')
    campos.forEach((c) => {
      if (
        c.depende_de &&
        c.depende_de.includes(campo.codigo_campo) &&
        valor &&
        c.inicializando === 'P' &&
        todosLosPadresTienenValor(c, campos, datos)
      ) {
        const valores = getValoresConsulta()
        dispatch(consultaInicial({ idpk: c.idpk, valores }))
      }
    })
    campo.acciones.forEach((action) => {
      if (
        action.valor_referencia === valor ||
        (action.valor_referencia === 'NOT_NULL' && valor) ||
        (action.valor_referencia === 'NULL' && !valor)
      ) {
        dispatch(executeFieldAction({ action }))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor])

  const handleError = (open, message, idpk) => {
    setAlertOpen(open)
    setValidationError(message)
    dispatch(setCampoError({ idpk: idpk, error: open }))
  }

  const handleChange = async (newValue) => {
    try {
      // Activar estado de validación
      dispatch(setCampoValidando({ idpk: campo.idpk, validando: true }))

      const result = await validate(campo, newValue, campos, datos)

      if (result?.warning) {
        setWarningDialog({ open: true, message: result.warning, pendingValue: newValue })
        return
      }

      processValidationResult(result, campo, newValue, dispatch)
      handleError(false, '', campo.idpk)
    } catch (error) {
      handleError(true, error.message, campo.idpk)
    } finally {
      dispatch(setCampoValidando({ idpk: campo.idpk, validando: false }))
    }
  }

  const handleWarningAccept = () => {
    processValidationResult(true, campo, warningDialog.pendingValue, dispatch)
    setWarningDialog({ open: false, message: '', pendingValue: null })
  }

  const handleWarningCancel = () => {
    setWarningDialog({ open: false, message: '', pendingValue: null })
  }

  if (campo && campo.visible === 'S') {
    const dates = validate(campo, true, campos, datos)

    return (
      <>
        <LocalizationProvider
          localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
          dateAdapter={AdapterDayjs}
          adapterLocale="es"
        >
          <Box sx={{ width: '100%' }}>
            <LabelComponent text={campo.etiqueta} obligatorio={campo.obligatorio} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CustomDateTimeComponent
                  campo={{
                    ...campo,
                    informativo: campo.informativo === 'S' || campo.validando ? 'S' : 'N',
                  }}
                  dates={dates}
                  valor={valor}
                  onChange={(v) => handleChange(v)}
                />
                {campo.cargando && <CircularProgress size={20} />}
              </Box>
              {campo.validando && (
                <FormHelperText>
                  <ValidatingText />
                </FormHelperText>
              )}
              <Collapse in={alertOpen}>
                <Alert
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setAlertOpen(false)
                      }}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2 }}
                  severity="error"
                >
                  <AlertTitle>Error</AlertTitle>
                  {validationError}
                </Alert>
              </Collapse>
            </Box>
          </Box>
        </LocalizationProvider>
        <CustomDialog
          open={warningDialog.open}
          title="Aviso"
          description={warningDialog.message}
          actionLeftText="Cancelar"
          actionLeft={handleWarningCancel}
          actionRightText="Aceptar"
          actionRight={handleWarningAccept}
          closeDialogEvent={handleWarningCancel}
          contentMinWidth="300px"
        />
      </>
    )
  }
}

export default DateComponent
